'use client';

import { usePathname } from 'next/navigation';
import { YandexMetricaProvider } from 'next-yandex-metrica';

import { ErrorBoundary } from '@/app/providers/error-boundary';
import { ComposeChildren } from '@/app/utils';
import { AuthProvider, MetricaUserSyncProvider } from '@/entities/user';
import { BreadcrumbsProvider } from '@/features/breadcrumbs';
import { ToastProvider } from '@/features/toast';
import { ConfirmProvider } from '@/shared';
import { UserV1 } from '@/shared/api';
import { YM_COUNTER_ID } from '@/shared/constants';
import {
	CodeTabProvider,
	ScreenProvider,
	UtmProvider,
} from '@/shared/providers';
import { isProduction } from '@/shared/utils';
import { SidebarProvider } from '@/widgets';
import {
	NavigationCareerOffer,
	NavigationCareerOfferProvider,
} from '@/features/navigation';
import { CareerOfferAttributionProvider } from '@/features/career-offer-analytics';

export function AppProviders({
	children,
	initialUser,
	navigationCareerOffer,
}: {
	children: React.ReactNode;
	initialUser?: UserV1 | null;
	navigationCareerOffer: NavigationCareerOffer | null;
}) {
	const isProd = isProduction();
	const path = usePathname();
	const hideChrome =
		path.startsWith('/free-interview/') ||
		path.startsWith('/promo/') ||
		/^\/course\/[^/]+\/register$/.test(path) ||
		path.startsWith('/interviews/') ||
		path.startsWith('/login') ||
		path.startsWith('/register') ||
		/^\/course\/[^/]+\/about$/.test(path);
	return (
		<ComposeChildren>
			<SidebarProvider />
			<BreadcrumbsProvider />
			<NavigationCareerOfferProvider offer={navigationCareerOffer} />
			<AuthProvider initialUser={initialUser} />
			<ToastProvider />
			<ScreenProvider />
			<CodeTabProvider />
			<ConfirmProvider />
			<UtmProvider />
			<CareerOfferAttributionProvider />
			{isProd && (
				<YandexMetricaProvider
					tagID={Number(YM_COUNTER_ID)}
					initParameters={{
						clickmap: true,
						trackLinks: true,
						accurateTrackBounce: true,
						webvisor: true,
					}}
				>
					{/* ts ругается, если не передавать children */}
					{''}
				</YandexMetricaProvider>
			)}
			<MetricaUserSyncProvider />
			<ErrorBoundary showHeader={!hideChrome} showFooter={!hideChrome}>
				{''}
			</ErrorBoundary>
			<>{children}</>
		</ComposeChildren>
	);
}
